import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

const Button3D = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  onClick,
  disabled = false,
  className = '',
  type = 'button',
  asChild,
  ...props
}) => {
  const [isPressed, setIsPressed] = useState(false);

  const variants = {
    primary: {
      base: 'bg-blue-500 text-white border-blue-600',
      hover: 'hover:bg-blue-600',
      shadow: '#1d4ed8'
    },
    secondary: {
      base: 'bg-gray-500 text-white border-gray-600',
      hover: 'hover:bg-gray-600',
      shadow: '#374151'
    },
    success: {
      base: 'bg-green-500 text-white border-green-600',
      hover: 'hover:bg-green-600',
      shadow: '#15803d'
    },
    danger: {
      base: 'bg-red-500 text-white border-red-600',
      hover: 'hover:bg-red-600',
      shadow: '#b91c1c'
    },
    warning: {
      base: 'bg-yellow-500 text-black border-yellow-600',
      hover: 'hover:bg-yellow-600',
      shadow: '#a16207'
    },
    red: {
      base: 'bg-[#e8001d] text-white border-red-700',
      hover: 'hover:bg-[#cc0019]',
      shadow: '#991f1f'
    },
    nav: {
      base: 'bg-transparent text-[#a3a3a3] border-transparent',
      hover: 'hover:text-[#f5f5f5] hover:bg-white/5',
      shadow: 'transparent'
    }
  };

  const sizes = {
    sm: 'px-3 py-2 text-xs sm:px-4 sm:py-2 sm:text-sm',
    md: 'px-4 py-2 text-sm sm:px-6 sm:py-3 sm:text-base',
    lg: 'px-6 py-3 text-base sm:px-8 sm:py-4 sm:text-lg'
  };

  const currentVariant = variants[variant] || variants.nav;
  const currentSize = sizes[size];

  const handleMouseDown = () => {
    if (!disabled) {
      setIsPressed(true);
    }
  };

  const handleMouseUp = () => {
    setIsPressed(false);
  };

  const handleClick = (e) => {
    if (onClick && !disabled) {
      onClick(e);
    }
  };

  return (
    <motion.button
      type={type}
      className={cn(
        currentVariant.base,
        currentVariant.hover,
        'font-bold',
        'rounded-md',
        'border-b-4',
        currentSize,
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        'focus:outline-none',
        'select-none',
        'flex',
        'items-center',
        'justify-center',
        'uppercase',
        'tracking-[0.15em]',
        className
      )}
      initial={{ 
        boxShadow: `0 4px 0 0 ${currentVariant.shadow}`,
        y: 0 
      }}
      whileHover={{ 
        scale: disabled ? 1 : 1.02,
        boxShadow: disabled ? `0 4px 0 0 ${currentVariant.shadow}` : `0 6px 0 0 ${currentVariant.shadow}`,
        transition: { duration: 0.1 }
      }}
      whileTap={{ 
        scale: disabled ? 1 : 0.98,
        y: disabled ? 0 : 2,
        boxShadow: disabled ? `0 4px 0 0 ${currentVariant.shadow}` : `0 2px 0 0 ${currentVariant.shadow}`,
        transition: { duration: 0.1 }
      }}
      animate={{
        y: isPressed ? 2 : 0,
        boxShadow: isPressed ? `0 2px 0 0 ${currentVariant.shadow}` : `0 4px 0 0 ${currentVariant.shadow}`
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 20
      }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onClick={handleClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </motion.button>
  );
};

export { Button3D };